<template>
  <div
    :key="refreshKey"
    class="flex flex-col items-center w-full gap-2 bg-white dark:bg-gray-800 rounded-lg shadow-xl shadow-blue-gray-900/5"
  >
    <VideoDropdownModal v-model:open="showVideoModal" :video-url="videoURL" />
    <div
      class="relative flex flex-col w-full font-regular p-8 portrait:px-2 portrait:py-5 py-10 text-gray-900 dark:text-gray-200 max-w-5xl"
    >
      <!-- Navigation Section -->
      <nav
        class="w-full flex flex-row portrait:flex-col landscape:justify-between justify-center portrait:gap-5 mb-6 landscape:items-center"
      >
        <!-- Back Button and Title -->
        <div
          class="w-full max-w-fit flex flex-row gap-5 items-center justify-start"
        >
          <button
            @click="goback"
            class="px-4 flex flex-col items-center justify-center aspect-square w-fit bg-blue-gray-500/10 hover:bg-blue-gray-500/20 border-blue-gray-600/10 rounded-lg dark:bg-gray-700 dark:hover:bg-gray-600 transition-all duration-300 ease-in-out transform hover:scale-105"
            aria-label="Go back"
          >
            <i class="fa-solid fa-arrow-left text-base dark:text-gray-200"></i>
          </button>
          <h3
            class="text-[clamp(18px,2vw,24px)] font-medium dark:text-gray-200"
          >
            User Profile
          </h3>
        </div>

        <!-- Watch Video Tutorial Button -->
        <button
          @click="handleOpenVideo"
          class="w-full landscape:max-w-fit flex gap-2 items-center justify-center px-5 py-4 bg-blue-gray-500/10 rounded-lg text-[14px] border-blue-gray-600/10 font-medium h-min dark:bg-gray-700 dark:hover:bg-gray-600 dark:border-gray-600 dark:text-gray-200 transition-all duration-300 ease-in-out transform hover:scale-105 hover:shadow-lg hover:bg-blue-gray-500/30"
        >
          <i
            class="fa-regular fa-circle-play dark:text-gray-200 transition-transform duration-300 ease-in-out transform hover:rotate-12"
          ></i>
          <span>Watch Video Tutorial</span>
        </button>
      </nav>
      <div
        class="w-full border-[1px] bg-gray-50 dark:bg-gray-700 border-gray-900/5 dark:border-gray-700 flex flex-col h-full rounded-lg relative"
      >
        <main class="p-6 lg:p-8">
          <header
            class="flex flex-col lg:flex-row lg:justify-between lg:items-center mb-6 gap-4"
          >
            <h1 class="text-2xl lg:text-3xl font-bold dark:text-gray-200"></h1>
            <div
              class="flex items-center gap-4 w-full lg:w-auto justify-between"
            >
              <div
                class="w-10 h-10 rounded-full bg-white dark:bg-gray-800 shadow-sm flex items-center justify-center cursor-pointer transition hover:-translate-y-0.5"
              >
                <i class="fas fa-bell dark:text-gray-200"></i>
              </div>
              <div
                class="w-10 h-10 rounded-full bg-primary dark:bg-secondary text-white flex items-center justify-center font-semibold cursor-pointer transition hover:-translate-y-0.5"
              >
                {{ userInitials }}
              </div>
            </div>
          </header>
          <div class="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <!-- Profile Card -->
            <!-- Profile Card -->
            <section
              class="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 flex flex-col items-center lg:col-span-4"
            >
              <div class="relative w-min h-24 mb-4">
                <div
                  class="w-24 h-24 rounded-full bg-primary-light dark:bg-sub-100 relative overflow-hidden"
                >
                  <img
                    :src="
                      user?.image && user.image !== 'profile.png'
                        ? getFileUrl(user.image)
                        : `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || 'User')}&background=cccccc&color=555555&size=128`
                    "
                    alt="Profile picture"
                    class="w-full h-full object-cover"
                  />
                </div>
                <div
                  class="w-8 h-8 rounded-full bg-primary dark:bg-secondary text-white flex items-center justify-center absolute bottom-0 right-0 cursor-pointer z-10"
                  @click="openEditProfilePicture"
                >
                  <i class="fas fa-pencil-alt text-xs"></i>
                </div>
              </div>

              <!-- Name with proper constraints -->
              <h2
                class="text-xl font-bold mb-1 dark:text-gray-200 text-center w-full px-4 max-w-full truncate"
              >
                {{ user?.name || "User Name" }}
              </h2>

              <!-- Email with proper constraints -->
              <p
                class="text-gray-dark mb-6 dark:text-gray-400 text-center w-full px-4 max-w-full truncate text-sm"
              >
                {{ user?.email || "user@example.com" }}
              </p>

              <div class="w-full px-2">
                <button
                  class="w-full bg-primary text-white py-3 px-4 rounded-md font-semibold text-sm flex items-center justify-center gap-2 transition hover:bg-[#5D4BCC] hover:shadow-md dark:bg-secondary dark:hover:bg-sub-800"
                  @click="openEditProfile"
                >
                  <i class="fas fa-edit"></i>
                  Edit Profile
                </button>
              </div>
            </section>

            <!-- Wallet Card -->
            <section
              class="bg-gradient-to-br from-[var(--main500)] to-[var(--main700)] dark:from-[#98cdf8] dark:to-[#76bdf6] dark:bg-gradient-to-br text-white rounded-xl shadow-light p-6 flex flex-col justify-between lg:col-span-8"
            >
              <div class="flex justify-between items-center mb-8">
                <h2 class="text-xl font-semibold dark:text-gray-900">
                  Wallet Balance
                </h2>
                <div class="flex gap-3">
                  <button
                    type="button"
                    class="bg-transparent text-white py-2 px-3 rounded-md text-sm border border-white/30 flex items-center justify-center transition hover:bg-white/10 dark:text-gray-900 dark:border-gray-900"
                    @click="openFundWallet"
                  >
                    <i class="fas fa-exchange-alt"></i>
                  </button>

                  <button
                    type="button"
                    @click="openTransactionHistory"
                    class="bg-transparent text-white py-2 px-3 rounded-md text-sm border border-white/30 flex items-center justify-center transition hover:bg-white/10 dark:text-gray-900 dark:border-gray-900"
                  >
                    <i class="fas fa-history"></i>
                  </button>
                </div>
              </div>

              <div class="mb-6">
                <div class="text-4xl font-bold mb-2 dark:text-gray-900">
                  ₦{{
                    wallet
                      ? Number(wallet?.balance)?.toLocaleString("en-NG", {
                          minimumFractionDigits: 2,
                        })
                      : "0.00"
                  }}
                </div>
                <p class="opacity-90 dark:text-gray-800" v-if="wallet?.bonus">
                  + ₦{{
                    Number(wallet.bonus)?.toLocaleString("en-NG", {
                      minimumFractionDigits: 2,
                    })
                  }}
                  bonus available
                </p>
                <p class="opacity-90 dark:text-gray-800" v-else>
                  No bonus available
                </p>
              </div>

              <div class="flex flex-col sm:flex-row gap-6">
                <div class="flex items-center">
                  <button
                    type="button"
                    class="bg-transparent text-white py-2 px-3 rounded-md text-sm flex items-center justify-center transition hover:bg-white/10 gap-2"
                    @click="openFundWallet"
                  >
                    <i class="fas fa-arrow-up"></i>
                    <span class="dark:text-gray-900">Fund Wallet</span>
                  </button>
                </div>
              </div>
            </section>

            <!-- Stores Card -->
            <section
              class="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 lg:p-8 lg:col-span-8 overflow-hidden"
            >
              <div
                class="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 gap-4"
              >
                <h2 class="text-xl font-semibold dark:text-gray-200 truncate">
                  My Stores
                </h2>
                <button
                  class="bg-primary dark:bg-secondary text-white py-2 px-4 rounded-md font-semibold text-sm flex items-center justify-center gap-2 transition hover:bg-main-800 dark:hover:bg-sub-800 hover:shadow-md flex-shrink-0"
                >
                  <i class="fas fa-plus"></i>
                  Add Store
                </button>
              </div>

              <div
                class="flex flex-col gap-3 max-h-[calc(100vh-200px)] overflow-y-auto"
              >
                <div
                  v-for="store in stores"
                  :key="store.id"
                  class="flex items-center justify-between p-4 rounded-md bg-gray-light dark:bg-gray-700 transition hover:-translate-y-0.5 hover:shadow-sm flex-shrink-0"
                >
                  <div class="flex items-center gap-3 min-w-0">
                    <div
                      class="w-10 h-10 rounded-md bg-primary-light dark:bg-sub-100 text-primary dark:text-secondary flex items-center justify-center flex-shrink-0"
                    >
                      <i class="fas fa-shop"></i>
                    </div>
                    <div class="min-w-0">
                      <div class="font-semibold dark:text-gray-200 truncate">
                        {{ store.store_name }}
                      </div>
                      <div
                        class="text-sm text-gray-dark dark:text-gray-400 truncate"
                      >
                        <a
                          :href="
                            store.store_url?.subDomain
                              ? `https://${store.store_url.subDomain}.hisell.shop`
                              : 'https://example.hisell.shop'
                          "
                          v-text="
                            store.store_url?.subDomain
                              ? `https://${store.store_url.subDomain}.hisell.shop`
                              : 'https://example.hisell.shop'
                          "
                          target="_blank"
                          rel="noopener noreferrer"
                          class="font-medium leading-normal text-main-900 dark:text-sub-400 truncate block"
                        ></a>
                      </div>
                    </div>
                  </div>
                  <div
                    class="px-3 py-1 rounded-full text-sm font-semibold bg-[rgba(0,184,148,0.1)] text-secondary dark:bg-[rgba(0,184,148,0.15)] flex-shrink-0"
                  >
                    Active
                  </div>
                </div>
              </div>
            </section>

            <!-- Subscription Card -->
            <section
              class="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 flex flex-col lg:col-span-4"
            >
              <template v-if="hasActivePlan">
                <!-- Active Plan Content -->
                <div class="flex items-start gap-3 mb-6">
                  <div
                    class="w-fit h-fit p-3 rounded-md flex items-center justify-center text-xl"
                    :class="{
                      'bg-primary-light dark:bg-sub-100 text-primary dark:text-secondary':
                        hasActivePlan,
                      'bg-gray-100 dark:bg-gray-700 text-gray-400':
                        !hasActivePlan,
                    }"
                  >
                    <i
                      class="fas"
                      :class="{
                        'fa-crown': hasActivePlan,
                        'fa-hourglass-half': !hasActivePlan,
                      }"
                    ></i>
                  </div>
                  <div>
                    <div class="font-semibold dark:text-gray-200">
                      {{ subscriptionDetails.name }}
                    </div>
                    <div
                      class="text-secondary text-sm font-medium dark:text-secondary"
                    >
                      {{
                        subscriptionEndDate
                          ? `Expires on ${subscriptionEndDate}`
                          : "NA"
                      }}
                    </div>
                  </div>
                </div>

                <div class="mb-6">
                  <div
                    v-for="(
                      feature, index
                    ) in subscriptionDetails?.features.slice(0, 3)"
                    :key="'feature-' + index"
                    class="flex items-center gap-2 mb-2 text-sm"
                  >
                    <i
                      class="fas fa-check text-secondary dark:text-secondary"
                    ></i>
                    <span class="dark:text-gray-200">{{ feature }}</span>
                  </div>
                </div>

                <div class="flex flex-col gap-3">
                  <RouterLink
                    to="/service-plans"
                    class="bg-transparent text-primary py-3 px-4 rounded-md font-semibold text-sm flex items-center justify-center gap-2 border border-primary transition hover:bg-primary-light dark:hover:bg-sub-100 dark:text-secondary dark:border-secondary"
                  >
                    <i class="fas fa-list-alt"></i> View Plans
                  </RouterLink>
                </div>
              </template>

              <template v-else>
                <!-- No Active Plan Content -->
                <div class="flex items-start gap-3 mb-6">
                  <div
                    class="w-fit h-fit p-3 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-400 flex items-center justify-center text-xl"
                  >
                    <i class="fas fa-crown"></i>
                  </div>
                  <div>
                    <div class="font-semibold dark:text-gray-200">
                      No Active Plan
                    </div>
                    <div class="text-gray-400 text-sm font-medium">
                      You don't have an active plan
                    </div>
                  </div>
                </div>

                <div class="mb-6 text-sm text-gray-600 dark:text-gray-300">
                  <p class="mb-4">
                    Upgrade to unlock premium features and grow your business.
                  </p>

                  <div class="flex items-center gap-2 mb-2">
                    <i class="fas fa-check text-gray-400"></i>
                    <span>Automated Marketing</span>
                  </div>
                  <div class="flex items-center gap-2 mb-2">
                    <i class="fas fa-check text-gray-400"></i>
                    <span>Unlimited Products</span>
                  </div>
                  <div class="flex items-center gap-2 mb-2">
                    <i class="fas fa-check text-gray-400"></i>
                    <span>Advanced Analytics</span>
                  </div>
                  <div class="flex items-center gap-2 mb-2">
                    <i class="fas fa-check text-gray-400"></i>
                    <span>Priority Support</span>
                  </div>
                </div>

                <div class="flex flex-col gap-3">
                  <RouterLink
                    to="/service-plans"
                    class="bg-primary text-white py-3 px-4 rounded-md font-semibold text-sm flex items-center justify-center gap-2 transition hover:bg-primary-dark dark:bg-secondary dark:hover:bg-secondary-dark"
                  >
                    <i class="fas fa-rocket"></i> Upgrade Now
                  </RouterLink>
                </div>
              </template>
            </section>

            <!-- Danger Zone -->
            <section
              class="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border-2 border-dashed border-danger lg:col-span-12"
            >
              <div class="flex items-center gap-3 mb-4">
                <div
                  class="w-10 h-10 rounded-full bg-[rgba(255,118,117,0.1)] dark:bg-[rgba(255,118,117,0.15)] text-danger flex items-center justify-center"
                >
                  <i class="fas fa-exclamation"></i>
                </div>
                <h2 class="text-danger font-semibold dark:text-danger">
                  Danger Zone
                </h2>
              </div>

              <p class="text-gray-dark mb-6 dark:text-gray-400">
                Deleting your account will permanently remove all your data
                including stores, products, and transaction history. This action
                cannot be undone.
              </p>

              <button
                class="bg-danger text-white py-3 px-4 rounded-md font-semibold text-sm flex items-center justify-center gap-2 transition hover:bg-[#E74C3C] hover:shadow-md"
                @click="openDeleteAccount"
              >
                <i class="fas fa-trash-alt"></i>
                Delete Account
              </button>
            </section>
          </div>
        </main>
      </div>
    </div>
    <ModalComponent v-model:dialogVisible="showModal">
      <template #modalContent>
        <component
          :is="currentModalComponent"
          v-bind="currentModalProps"
          @close="showModal = false"
        />
      </template>
    </ModalComponent>
  </div>
</template>

<script>
import ModalComponent from "@/components/modalComponent.vue";
import EditProfilePictureComponent from "@/components/editProfilePictureComponent.vue";
import EditProfileComponent from "@/components/editProfileComponent.vue";
import TransactionHistoryComponent from "@/components/transactionHistoryComponent.vue";
import FundWalletComponent from "@/components/fundWalletComponent.vue";
import DeleteAccountComponent from "@/components/deleteAccountComponent.vue";
import ViewPlansComponent from "@/components/viewPlansComponent.vue";
import { markRaw } from "vue";
import { mapActions, mapGetters } from "vuex";
import eventBus from "@/utils/eventBus";
export default {
  name: "ProfileView",
  inject: ["getFileUrl"],
  data() {
    return {
      subscriptionDetails: {
        name: "Basic Plan",
        features: [
          "Unlimited Stores",
          "Unlimited Products",
          "Advanced analytics",
          "Custom payment options",
          "Priority support",
          "Custom payment options",
          "Custom delivery email",
        ],
        status: "inactive",
      },
      showModal: false,
      currentModalComponent: null,
      currentModalProps: {},
      hasActivePlan: false,
      subscriptionEndDate: null,
      showVideoModal: false,
      videoURL: "https://www.youtube.com/watch?v=9yvQTR2siPM",
    };
  },
  provide() {
    return {
      helpVideoURL: this.videoURL,
    };
  },
  beforeUnmount() {
    eventBus.off("open-help-video", this.handleOpenVideo);
  },
  computed: {
    ...mapGetters(["subscription"]),
    user() {
      return this.$store.getters.userDetails;
    },
    wallet() {
      return this.$store.getters.wallet;
    },
    stores() {
      return this.$store.getters.allStores;
    },
    selectedStore() {
      return this.$store.getters.selectedStore;
    },
    userInitials() {
      if (!this.user || !this.user.name) return "U";
      const names = this.user.name.trim().split(" ");
      if (names.length === 1) return names[0][0].toUpperCase();
      return (names[0][0] + names[names.length - 1][0]).toUpperCase();
    },
  },
  mounted() {
    this.subscriptionData();
    eventBus.on("open-help-video", this.handleOpenVideo);
  },
  watch: {
    subscription: {
      immediate: true,
      async handler(newVal) {
        if (newVal) {
          try {
            await this.subscriptionData();
          } catch (error) {
            console.error("Error in subscription watcher:", error);
          }
        }
      },
    },
    "$store.state.storeUpdatedAt": {
      immediate: true,
      handler(val) {
        this.refreshKey = val;
      },
    },
  },
  methods: {
    ...mapActions(["fetchServicePlans"]),
    goback() {
      this.$router.go(-1);
    },
    handleOpenVideo() {
      this.showVideoModal = true;
    },
    async subscriptionData() {
      const response = await this.fetchServicePlans();
      if (!response.success) {
        console.error("Failed to fetch plans:", response?.error);
        return;
      }

      const plans = response?.value || [];
      const subscriptions = this.subscription;
      const subscription =
        subscriptions?.find((sub) => sub.storeId === this.selectedStore.id)
          ?.subscription || null;
      const plan_id = subscription?.plan_id || null;

      this.hasActivePlan = subscription && subscription.status === "active";
      this.subscriptionEndDate = subscription?.end_date
        ? this.formatExpiryDate(subscription.end_date)
        : null;

      const selectedPlan = plans.find((plan) => plan.id === plan_id);

      this.subscriptionDetails = {
        name: selectedPlan?.name || "",
        features: selectedPlan?.features || [],
      };
    },
    // Helper function to format date as "Oct 2023"
    formatExpiryDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        month: "short", // "Oct" instead of "October"
        year: "numeric",
      });
    },
    openEditProfilePicture() {
      this.currentModalComponent = markRaw(EditProfilePictureComponent);
      this.currentModalProps = {
        currentImage: this.user?.image,
      };
      this.showModal = true;
    },
    openEditProfile() {
      this.currentModalComponent = markRaw(EditProfileComponent);
      console.log(this.user, this.wallet);
      this.currentModalProps = {
        profileData: {
          name: this.user?.name || "",
          email: this.user?.email || "",
          about: this.user?.about || "",
        },
      };
      this.showModal = true;
    },
    openTransactionHistory() {
      this.currentModalComponent = markRaw(TransactionHistoryComponent);
      this.currentModalProps = {};
      this.showModal = true;
    },
    openFundWallet() {
      this.currentModalComponent = markRaw(FundWalletComponent);
      this.currentModalProps = {};
      this.showModal = true;
    },
    openDeleteAccount() {
      this.currentModalComponent = markRaw(DeleteAccountComponent);
      this.currentModalProps = {};
      this.showModal = true;
    },
    renewSubscription() {
      // Logic to renew subscription
      this.$toast.success("Subscription renewed successfully!");
    },
    openServicePlans() {
      this.currentModalComponent = markRaw(ViewPlansComponent);
      this.currentModalProps = {};
      this.showModal = true;
    },
  },
  components: {
    ModalComponent,
    EditProfilePictureComponent,
    EditProfileComponent,
    TransactionHistoryComponent,
    FundWalletComponent,
    DeleteAccountComponent,
    // ...
  },
};
</script>
<style scoped>
.gradient-primary {
  background: linear-gradient(135deg, var(--main500) 0%, var(--main700) 100%);
}
.shadow-light {
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
}
</style>
